import { useCallback, useEffect, useRef, useState } from "react";
import { isApiProblem, problemMessage } from "../../shared/api/problem";
import type { Locale } from "../../shared/api/types";
import { conditionalMediationAvailable, credentialToJson, isCancelled, requestOptionsFromJson, webAuthnAvailable } from "../../shared/auth/webauthn";
import { authText } from "../../shared/i18n/authText";
import { passkeyLogin, passkeyLoginOptions } from "./api";

/**
 * A ceremony lives 5 minutes on the server; the waiting autofill request is renewed a little before that, so a passkey picked
 * from the autofill after a long pause is never answered with a stale-ceremony 401.
 */
export const CONDITIONAL_RENEW_MS = 4 * 60_000;

/** A failed passkey sign-in in words. Cancelling the browser prompt is not a failure and never reaches here. */
export function passkeyError(error: unknown, locale: Locale): string {
  if (isApiProblem(error)) {
    // Every verification failure is one 401 (§1.6): an unknown or removed passkey, another environment's passkey (a UAT
    // passkey on PRD), a partner. One plain sentence that says what to do instead.
    if (error.status === 401) return authText("passkeyNotSetUp", locale);
    if (error.status === 429 || error.code === "rate-limited") return authText("loginRateLimited", locale);
    // Only with Passkeys__AllowWhileLocked=false.
    if (error.status === 423 || error.code === "account-locked") return authText("loginLocked", locale);
    if (error.isFeatureDisabled) return authText("passkeyUnavailable", locale);
  }
  return problemMessage(error, locale);
}

interface Handlers {
  locale: Locale;
  onSignedIn: () => void | Promise<void>;
  onBusy: (busy: boolean) => void;
  onError: (message: string | null) => void;
}

/**
 * One passkey request at a time, as the browser allows: the waiting autofill (conditional) request, or the button's prompt.
 * Plain object, so the async loop lives outside React's render; the hook below owns its lifetime.
 */
class PasskeySignInController {
  private waiting: { controller: AbortController; done: Promise<void>; renew: ReturnType<typeof setTimeout> } | null = null;
  private disposed = false;
  private conditional = false;

  constructor(private readonly handlers: () => Handlers) {}

  async start(): Promise<void> {
    if (!(await conditionalMediationAvailable()) || this.disposed) return;
    this.conditional = true;
    this.wait();
  }

  dispose(): void {
    this.disposed = true;
    if (this.waiting) {
      clearTimeout(this.waiting.renew);
      this.waiting.controller.abort();
      this.waiting = null;
    }
  }

  /** A password submit: the waiting request goes (it is renewed if that sign-in fails and the page stays). */
  cancelWaiting(): void {
    void this.stopWaiting();
  }

  /** A password sign-in failed and the page stays: the autofill offers the passkey again. */
  resume(): void {
    if (this.conditional) this.wait();
  }

  /** The button: the browser's own passkey prompt. */
  async button(): Promise<void> {
    const h = this.handlers();
    h.onError(null);
    h.onBusy(true);
    await this.stopWaiting();
    let ok = false;
    try {
      ok = await this.ceremony("optional", new AbortController());
    } catch (caught) {
      if (!this.disposed && !isCancelled(caught)) this.handlers().onError(passkeyError(caught, this.handlers().locale));
    } finally {
      if (!this.disposed) this.handlers().onBusy(false);
    }
    // Cancelled or refused, and still on the page: offer the autofill again.
    if (!ok && !this.disposed && this.conditional) this.wait();
  }

  /** Options → browser → verify. True when signed in; false when the browser returned nothing or the request was aborted. */
  private async ceremony(mediation: "conditional" | "optional", controller: AbortController): Promise<boolean> {
    const { ceremonyId, publicKey } = await passkeyLoginOptions();
    if (controller.signal.aborted || this.disposed) return false;
    const credential = (await navigator.credentials.get({
      publicKey: requestOptionsFromJson(publicKey),
      signal: controller.signal,
      ...(mediation === "conditional" ? { mediation: "conditional" as CredentialMediationRequirement } : {}),
    })) as PublicKeyCredential | null;
    if (!credential || controller.signal.aborted || this.disposed) return false;
    if (mediation === "conditional") this.handlers().onBusy(true);
    await passkeyLogin(ceremonyId, credentialToJson(credential));
    if (this.disposed) return true;
    this.handlers().onError(null);
    await this.handlers().onSignedIn();
    return true;
  }

  private async stopWaiting(): Promise<void> {
    const current = this.waiting;
    if (!current) return;
    this.waiting = null;
    clearTimeout(current.renew);
    current.controller.abort();
    await current.done;
  }

  /** Starts (or renews) the waiting autofill request. */
  private wait(): void {
    if (this.disposed || this.waiting) return;
    const controller = new AbortController();
    const mine = () => this.waiting?.controller === controller;
    const renew = setTimeout(() => {
      if (!mine()) return;
      void this.stopWaiting().then(() => this.wait());
    }, CONDITIONAL_RENEW_MS);
    const done = this.ceremony("conditional", controller).then(
      (ok) => {
        if (!mine()) return;
        clearTimeout(renew);
        this.waiting = null;
        if (!ok) this.wait();
      },
      (caught: unknown) => {
        if (!mine()) return;
        clearTimeout(renew);
        this.waiting = null;
        const h = this.handlers();
        h.onBusy(false);
        if (isCancelled(caught)) return;
        h.onError(passkeyError(caught, h.locale));
        // A refused passkey (e.g. one removed from a profile) leaves the autofill working for the next try.
        if (isApiProblem(caught) && caught.status === 401) this.wait();
      },
    );
    this.waiting = { controller, done, renew };
  }
}

/**
 * Passkey sign-in on the login page (batch 18 GRF-46, design §1.12). Where the browser supports conditional UI, a request
 * waits from page load so the e-mail field's autofill offers the passkey; the "Sign in with a passkey" button aborts it and
 * asks with the browser's own prompt. Both end like a password sign-in (`onSignedIn`). `enabled` = the Passkeys flag.
 */
export function usePasskeySignIn({ enabled, locale, onSignedIn }: { enabled: boolean; locale: Locale; onSignedIn: () => void | Promise<void> }) {
  const available = enabled && webAuthnAvailable();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef<Handlers>({ locale, onSignedIn, onBusy: setBusy, onError: setError });
  useEffect(() => {
    latest.current = { locale, onSignedIn, onBusy: setBusy, onError: setError };
  }, [locale, onSignedIn]);
  const controller = useRef<PasskeySignInController | null>(null);

  useEffect(() => {
    if (!available) return;
    const created = new PasskeySignInController(() => latest.current);
    controller.current = created;
    void created.start();
    return () => {
      created.dispose();
      if (controller.current === created) controller.current = null;
    };
  }, [available]);

  const signInWithButton = useCallback(() => {
    void controller.current?.button();
  }, []);
  const cancelWaiting = useCallback(() => controller.current?.cancelWaiting(), []);
  const resumeWaiting = useCallback(() => controller.current?.resume(), []);

  return { available, busy, error, signInWithButton, cancelWaiting, resumeWaiting };
}
